import { useCallback, useContext, useState, useSyncExternalStore } from "react"
import { toast } from "sonner"
import { nextBenefitEditSession, type BenefitEditSession } from "@/lib/benefit-save-operation"
import { useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import { BenefitEditorContext } from "@/features/benefit-workspace/benefitEditorContext"
import type { EditorKey } from "@/features/benefit-workspace/benefitEditorController"

const noSubscribe = () => () => {}
/** Stable, Redux-owned benefit sessions; shared non-workspace controls retain their own scope. */
export function useBenefitEdit(latestToken: string | null, key?: EditorKey) {
  const context = useContext(BenefitEditorContext)
  const [session, setSession] = useState<BenefitEditSession>({ editing: false, token: null })
  const register = useBenefitEditRegistration()
  useSyncExternalStore(context?.store.subscribe ?? noSubscribe, () => {
    const active = context?.owned()
    return active && active.key === key ? `${active.sessionId}/${active.phase}` : ""
  }, () => "")
  if (context && !key) throw Error("Benefit workspace editors require a stable editor key.")
  const setEditing = useCallback((editing: boolean) => {
    if (context && key) {
      const accepted = editing ? context.open(key, latestToken) : context.close(key)
      if (!accepted) toast.error("Finish or review the active operation before opening or closing this editor.")
      return accepted
    }
    if (!register(editing)) {
      toast.error("Save or cancel the open benefit editor before opening another one.")
      return false
    }
    setSession(current => nextBenefitEditSession(current, editing, latestToken))
    return true
  }, [context, key, latestToken, register])
  const active = context?.owned()
  const editing = context ? active?.key === key : session.editing
  const token = context ? (editing ? active!.token : latestToken) : (session.editing ? session.token : latestToken)
  return [editing, setEditing, token] as const
}
